"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { PopupButton } from "react-calendly";
import { topgeoApiClient } from "@/library/api";
import { urlSchema } from "@/library/schemas";

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  onError?: (error: string) => void;
  onOpenBookDemo?: () => void; // Callback to open BookDemo modal in parent
}

interface CompetitorData {
  id: number;
  name: string;
  domain: string;
  logo_base64?: string;
}

interface PromptData {
  prompt: string;
  intent: string;
  relevance_score: number;
}

// Dummy data for competitors
const INITIAL_COMPETITORS = [];

// Dummy data for prompts
const MOCK_PROMPTS = [];

type ModalStep = "loading" | "confirm" | "competitors" | "prompts" | "success";

export default function ReportModal({
  isOpen,
  onClose,
  userEmail,
  onError,
  onOpenBookDemo,
}: ReportModalProps) {
  const [currentStep, setCurrentStep] = useState<ModalStep>("loading");
  const [competitors, setCompetitors] = useState(INITIAL_COMPETITORS);
  const [selectedCompetitors, setSelectedCompetitors] = useState<number[]>([]);
  const [newCompetitorName, setNewCompetitorName] = useState("");
  const [newCompetitorDomain, setNewCompetitorDomain] = useState("");
  const [prompts, setPrompts] = useState<string[]>(MOCK_PROMPTS);
  const [selectedPrompts, setSelectedPrompts] = useState<number[]>([]); // Track selected prompt indices
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [promptError, setPromptError] = useState("");
  const [newPromptText, setNewPromptText] = useState("");
  const [competitorError, setCompetitorError] = useState("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [editingCompanyName, setEditingCompanyName] = useState("");
  const [editingCompanyDomain, setEditingCompanyDomain] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const prevIsOpenRef = useRef(false);

  // Auto-transition from loading to competitors
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (isOpen && currentStep === "loading" && userEmail) {
        setIsLoadingData(true);
        setApiError(null);

        try {
          const response = await topgeoApiClient.post(
            "/lead-generation/analyze",
            {
              email: userEmail,
            }
          );

          if ((response.data as any).success && (response.data as any).data) {
            const apiData = (response.data as any).data;

            // Extract company info
            if (apiData.company_name) {
              setCompanyName(apiData.company_name);
              setEditingCompanyName(apiData.company_name);
            }
            if (apiData.company_logo_base64) {
              setCompanyLogo(apiData.company_logo_base64);
            }
            if (apiData.company_domain) {
              setCompanyDomain(apiData.company_domain);
              setEditingCompanyDomain(apiData.company_domain);
            }

            // Map competitors from API response
            if (apiData.competitors && apiData.competitors.length > 0) {
              const mappedCompetitors = apiData.competitors
                .slice(0, 10)
                .map((comp: any, index: number) => ({
                  id: index + 1,
                  name: comp.name,
                  domain: comp.domain,
                  logo: comp.logo_base64 ? comp.logo_base64 : "🏢",
                }));
              setCompetitors(mappedCompetitors);
              // Only select up to min(3, number of competitors)
              const numToSelect = Math.min(3, mappedCompetitors.length);
              setSelectedCompetitors(
                mappedCompetitors.slice(0, numToSelect).map((c) => c.id)
              );
            } else {
              // No competitors from API - reset to empty
              setCompetitors([]);
              setSelectedCompetitors([]);
            }

            // Map prompts from API response
            if (
              apiData.customer_search_prompts &&
              apiData.customer_search_prompts.length > 0
            ) {
              const mappedPrompts = apiData.customer_search_prompts
                .slice(0, 5)
                .map((p: any) => p.prompt);
              setPrompts(mappedPrompts);
              // Only select up to min(3, number of prompts)
              const numToSelect = Math.min(3, mappedPrompts.length);
              setSelectedPrompts(
                Array.from({ length: numToSelect }, (_, i) => i)
              );
            } else {
              // No prompts from API - reset to empty
              setPrompts([]);
              setSelectedPrompts([]);
            }

            setTimeout(() => {
              setCurrentStep("confirm");
              setIsLoadingData(false);
            }, 1000);
          } else {
            throw new Error("Invalid API response");
          }
        } catch (error: any) {
          console.error("Error fetching company data:", error);

          // Extract error message from API response
          let errorMessage = "Unable to fetch company data. Please try again.";
          let isEmailError = false;

          if (error.response?.data?.error) {
            // API returned a specific error message
            errorMessage = error.response.data.error;
            // Check if it's an email-related error
            if (
              errorMessage.toLowerCase().includes("email") ||
              errorMessage.toLowerCase().includes("professional") ||
              errorMessage.toLowerCase().includes("company")
            ) {
              isEmailError = true;
            }
          } else if (error.response?.status === 400) {
            errorMessage = "Invalid request. Please check your email address.";
            isEmailError = true;
          } else if (error.response?.status === 404) {
            errorMessage = "Service not found. Please contact support.";
          } else if (error.response?.status >= 500) {
            errorMessage = "Server error. Please try again later.";
          } else if (error.message) {
            errorMessage = error.message;
          }

          // If it's an email error, close modal and pass error to parent
          if (isEmailError && onError) {
            onError(errorMessage);
            onClose();
          } else {
            // For other errors, show in modal
            setApiError(errorMessage);
          }

          setIsLoadingData(false);
        }
      }
    };

    if (isOpen && currentStep === "loading") {
      fetchCompanyData();
    }
  }, [isOpen, currentStep, userEmail]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep("loading");
      setCompetitors(INITIAL_COMPETITORS);
      setSelectedCompetitors([]);
      setNewCompetitorName("");
      setNewCompetitorDomain("");
      setPrompts(MOCK_PROMPTS);
      setSelectedPrompts([]);
      setEditingIndex(null);
      setEditText("");
      setEditingCompanyName("");
      setEditingCompanyDomain("");
      setConfirmError("");
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen]);

  const toggleCompetitor = (id: number) => {
    setSelectedCompetitors((prev) => {
      if (prev.includes(id)) {
        return prev.filter((cId) => cId !== id);
      } else if (prev.length < 3) {
        return [...prev, id];
      }
      return prev;
    });
  };

  const togglePrompt = (index: number) => {
    setSelectedPrompts((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else if (prev.length < 3) {
        return [...prev, index];
      }
      return prev;
    });
  };

  const handleDeleteCompetitor = (id: number) => {
    if (competitors.length > 1) {
      setCompetitors(competitors.filter((comp) => comp.id !== id));
      // Remove from selected if it was selected
      setSelectedCompetitors((prev) => prev.filter((cId) => cId !== id));
    }
  };

  const handleAddCompetitor = async () => {
    setCompetitorError("");
    if (!newCompetitorName.trim()) {
      return;
    }

    const domainResult = urlSchema.safeParse(newCompetitorDomain);
    if (!domainResult.success) {
      setCompetitorError(domainResult.error.issues[0].message);
      return;
    }

    if (competitors.length < 10) {
      setIsLoadingData(true);

      let logoToUse = "🏢"; // Default emoji icon

      try {
        // Fetch logo from API
        const response = await topgeoApiClient.get(
          `/logo/${newCompetitorDomain.trim()}`
        );

        const responseData = response.data as any;

        if (
          responseData.success &&
          responseData.data &&
          responseData.data.logo_base64
        ) {
          logoToUse = responseData.data.logo_base64;
        }
      } catch (error) {
        // If API fails or logo not found, use default emoji
        console.log("Logo fetch failed, using default icon");
      }

      const newId = Math.max(...competitors.map((c) => c.id), 0) + 1;
      const newCompetitor = {
        id: newId,
        name: newCompetitorName.trim(),
        domain: newCompetitorDomain.trim(),
        logo: logoToUse,
      };

      setCompetitors([...competitors, newCompetitor]);
      if (selectedCompetitors.length < 3) {
        setSelectedCompetitors([...selectedCompetitors, newId]);
      }
      setNewCompetitorName("");
      setNewCompetitorDomain("");
      setIsLoadingData(false);
    }
  };

  const handleEditPrompt = (index: number) => {
    setEditingIndex(index);
    setEditText(prompts[index]);
  };

  const handleSavePrompt = () => {
    if (editingIndex !== null) {
      if (!editText.trim()) {
        setPromptError("Please enter a prompt");
        return;
      }
      const newPrompts = [...prompts];
      newPrompts[editingIndex] = editText.trim();
      setPrompts(newPrompts);
      setEditingIndex(null);
      setEditText("");
      setPromptError("");
    }
  };

  const handleAddPrompt = () => {
    if (!newPromptText.trim()) {
      return;
    }

    if (prompts.length < 10) {
      setPrompts([...prompts, newPromptText.trim()]);
      setNewPromptText(""); // Clear input after adding
      setPromptError(""); // Clear any existing errors
    }
  };

  const handleDeletePrompt = (index: number) => {
    if (prompts.length > 1) {
      setPrompts(prompts.filter((_, i) => i !== index));
      // Update selected prompts indices after deletion
      setSelectedPrompts((prev) =>
        prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
      );
    }
  };

  const handleConfirmCompany = () => {
    // Validate inputs
    if (!editingCompanyName.trim()) {
      setConfirmError("Company name is required");
      return;
    }
    if (!editingCompanyDomain.trim()) {
      setConfirmError("Company domain is required");
      return;
    }

    // Update the actual company values with edited values
    setCompanyName(editingCompanyName.trim());
    setCompanyDomain(editingCompanyDomain.trim());
    setConfirmError("");
    setCurrentStep("competitors");
  };

  const handleContinueToPrompts = () => {
    setCurrentStep("prompts");
  };

  const handleBackToCompetitors = () => {
    setCurrentStep("competitors");
  };

  const handleGenerateReport = async () => {
    // Get selected competitor details
    const selectedCompetitorDetails = competitors
      .filter((comp) => selectedCompetitors.includes(comp.id))
      .map((comp) => ({
        id: comp.id,
        name: comp.name,
        domain: comp.domain,
      }));

    // Get selected prompt texts
    const selectedPromptTexts = selectedPrompts.map((index) => prompts[index]);

    // Create payload
    const payload = {
      email: userEmail,
      company: {
        name: companyName,
        domain: companyDomain,
      },
      competitors: selectedCompetitorDetails,
      prompts: selectedPromptTexts,
      metadata: {
        totalCompetitors: competitors.length,
        selectedCompetitorsCount: selectedCompetitors.length,
        totalPrompts: prompts.length,
        selectedPromptsCount: selectedPrompts.length,
        timestamp: new Date().toISOString(),
      },
    };

    console.log("=== GENERATE REPORT PAYLOAD ===");
    console.log(JSON.stringify(payload, null, 2));
    console.log("================================");

    setIsGeneratingReport(true);
    setApiError(null);

    try {
      const response = await topgeoApiClient.post(
        "/landing-page/generate-report",
        payload
      );

      console.log("=== API RESPONSE ===");
      console.log(JSON.stringify(response.data, null, 2));
      console.log("====================");

      // Type-safe check for success
      const responseData = response.data as any;

      if (responseData.success) {
        // Success - move to success step
        setCurrentStep("success");
      } else {
        // API returned success: false
        setApiError(
          responseData.message || "Failed to generate report. Please try again."
        );
      }
    } catch (error: any) {
      console.error("Error generating report:", error);
      console.error("Error response:", error.response?.data);

      let errorMessage = "Failed to generate report. Please try again.";

      if (error.response) {
        // Server responded with error
        const errorData = error.response.data;

        if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.status === "error" && errorData?.message) {
          // Handle backend error format
          errorMessage = errorData.message;
        } else if (error.response.status === 400) {
          // JSON parsing or validation error
          if (errorData?.message?.includes("JSON")) {
            errorMessage =
              "Invalid data format. Please try again or contact support.";
          } else {
            errorMessage =
              "Invalid request. Please check your information and try again.";
          }
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (error.response.status === 404) {
          errorMessage = "Service not available. Please contact support.";
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage =
          "Unable to connect to server. Please check your internet connection.";
      } else {
        // Error setting up request
        errorMessage = error.message || "An unexpected error occurred.";
      }

      setApiError(errorMessage);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleClose = () => {
    // Reset all modal state
    setCurrentStep("loading");
    setCompetitors(INITIAL_COMPETITORS);
    setSelectedCompetitors([]);
    setNewCompetitorName("");
    setNewCompetitorDomain("");
    setPrompts(MOCK_PROMPTS);
    setSelectedPrompts([]);
    setEditingIndex(null);
    setEditText("");
    setApiError(null);
    setCompanyName("");
    setCompanyLogo("");
    setCompanyDomain("");
    setNewPromptText(""); // Clear prompt input field
    setEditingCompanyName("");
    setEditingCompanyDomain("");
    setConfirmError("");

    // Close the modal
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <div
          className="bg-surface-raised rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl py-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Close Button */}
          <div className="relative p-4 border-b border-hairline">
            <button
              onClick={handleClose}
              className={`absolute ${
                currentStep === "loading" ? "-top-1" : "top-3"
              } right-3 text-ink-faint hover:text-ink-muted text-2xl cursor-pointer transition-colors z-10`}
            >
              ×
            </button>

            {/* Header Layout: Welcome (Left) | Logo + Name (Center) | Progress (Right) */}
            {currentStep !== "loading" && (
              <div className="flex items-center justify-between pt-2 px-2">
                {/* Left: Welcome Message + Company Logo and Name */}
                <div className="flex items-center gap-3">
                  {(companyName || companyLogo) && (
                    <>
                      {/* <p className="text-xs font-raleway font-bold text-ink-muted">Welcome!</p> */}
                      {companyLogo && (
                        <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 bg-surface-raised border border-hairline">
                          <img
                            src={companyLogo}
                            alt={companyName || "Company Logo"}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {companyDomain && (
                        <h3 className="text-sm font-raleway font-bold text-ink">
                          {companyDomain}
                        </h3>
                      )}
                    </>
                  )}
                </div>

                {/* Right: Progress Indicator */}
                <div className="flex items-center justify-end gap-2 px-4">
                  <div
                    className={`h-2 w-2 rounded-full ${
                      currentStep === "confirm" ? "bg-ink" : "bg-ink/20"
                    }`}
                  />
                  <div
                    className={`h-2 w-2 rounded-full ${
                      currentStep === "competitors" ? "bg-ink" : "bg-ink/20"
                    }`}
                  />
                  <div
                    className={`h-2 w-2 rounded-full ${
                      currentStep === "prompts" ? "bg-ink" : "bg-ink/20"
                    }`}
                  />
                  <div
                    className={`h-2 w-2 rounded-full ${
                      currentStep === "success" ? "bg-ink" : "bg-ink/20"
                    }`}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Step A: Loading */}
          {currentStep === "loading" && (
            <div className="p-12 flex flex-col items-center justify-center">
              {apiError ? (
                <>
                  {/* Error State */}
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-5">
                    <svg
                      className="w-8 h-8 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-raleway font-semibold text-ink text-center mb-2">
                    Oops! Something went wrong
                  </h3>
                  <p className="text-ink-muted font-raleway text-sm text-center mb-5 max-w-md">
                    {apiError}
                  </p>
                  <button
                    onClick={onClose}
                    className="px-6 py-3 bg-ink text-surface rounded-lg font-raleway font-semibold text-sm hover:bg-ink/90 transition-colors cursor-pointer"
                  >
                    Close & Try Again
                  </button>
                </>
              ) : (
                <>
                  {/* Loading State */}
                  <div className="w-16 h-16 border-4 border-hairline border-t-ink rounded-full animate-spin mb-5" />
                  <h3 className="text-lg font-raleway font-semibold text-ink text-center">
                    Analyzing your brand...
                  </h3>
                  <p className="text-ink-muted font-raleway text-xs mt-2 text-center">
                    We're identifying your top competitors
                  </p>
                </>
              )}
            </div>
          )}

          {/* Step B: Confirm Company Details */}
          {currentStep === "confirm" && (
            <div className="px-5 py-4">
              {/* Header */}
              <div className="text-center mb-3">
                <h2 className="text-lg font-raleway font-bold text-ink mb-1">
                  Confirm Your Company Details
                </h2>
                <p className="text-ink-muted font-raleway text-xs">
                  Please verify the details below before proceeding.
                </p>
              </div>

              {/* AI Generated Notice */}
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                <svg
                  className="w-4 h-4 text-amber-600 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-xs text-amber-700 font-raleway">
                  <span className="font-semibold">AI-Generated:</span> Cross-check and correct any inaccuracies.
                </p>
              </div>

              {/* Editable Fields */}
              <div className="space-y-3 mb-4">
                {/* Company Name Field */}
                <div>
                  <label className="block text-xs font-semibold text-ink font-raleway mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={editingCompanyName}
                    onChange={(e) => {
                      setEditingCompanyName(e.target.value);
                      setConfirmError("");
                    }}
                    placeholder="Enter your company name"
                    className="w-full px-3 py-2 border-2 border-hairline-strong rounded-lg focus:outline-none focus:border-ink font-raleway text-sm text-ink transition-colors"
                  />
                </div>

                {/* Company Domain Field */}
                <div>
                  <label className="block text-xs font-semibold text-ink font-raleway mb-1">
                    Company Domain
                  </label>
                  <input
                    type="text"
                    value={editingCompanyDomain}
                    onChange={(e) => {
                      setEditingCompanyDomain(e.target.value);
                      setConfirmError("");
                    }}
                    placeholder="e.g., example.com"
                    className="w-full px-3 py-2 border-2 border-hairline-strong rounded-lg focus:outline-none focus:border-ink font-raleway text-sm text-ink transition-colors"
                  />
                </div>
              </div>

              {/* Error Message */}
              {confirmError && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-xs font-raleway text-center">
                    {confirmError}
                  </p>
                </div>
              )}

              {/* Confirm Button */}
              <button
                onClick={handleConfirmCompany}
                className="w-full bg-ink text-surface py-2 px-6 rounded-lg font-raleway font-semibold text-sm hover:bg-ink/90 transition-colors cursor-pointer"
              >
                Confirm & Continue
              </button>
            </div>
          )}

          {/* Step C: Competitor Selection */}
          {currentStep === "competitors" && (
            <div
              className="flex flex-col"
              style={{ maxHeight: "calc(90vh - 80px)" }}
            >
              <div className="px-6 pt-3 pb-1">
                <h2 className="text-lg font-raleway font-bold text-ink mb-1 text-center">
                  We think they are your top competitors.
                </h2>
                <p className="text-ink-muted font-raleway text-xs mb-1 text-center">
                  Select up to 3 competitors to include in your report
                </p>
                <div className="flex justify-center mb-2">
                  <span
                    className={`text-xs font-raleway font-semibold px-3 py-1 rounded-full ${
                      selectedCompetitors.length === 0
                        ? "bg-red-100 text-red-700"
                        : selectedCompetitors.length === 3
                        ? "bg-green-100 text-green-700"
                        : "bg-surface text-ink-muted"
                    }`}
                  >
                    {selectedCompetitors.length === 0
                      ? "Please select at least 1"
                      : `${selectedCompetitors.length} / 3 selected`}
                  </span>
                </div>
              </div>

              {/* Scrollable Competitor Grid */}
              <div
                className="flex-1 overflow-y-auto px-6"
                style={{ maxHeight: "calc(90vh - 320px)" }}
              >
                {competitors.length === 0 ? (
                  /* Empty State */
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-10 h-10 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-raleway font-bold text-ink mb-2 text-center">
                      No Competitors Found
                    </h3>
                    <p className="text-sm text-ink-muted font-raleway text-center mb-4 max-w-md">
                      We couldn't automatically identify your competitors.
                      Please add at least 1 competitor below. You can select up
                      to 3 to track in your report.
                    </p>
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-2">
                      <svg
                        className="w-5 h-5 text-blue-600 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-xs text-blue-800 font-raleway">
                        <strong>Tip:</strong> Add competitors you'd like to
                        benchmark against in AI search results
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 py-4">
                    {competitors.map((competitor) => (
                      <div
                        key={competitor.id}
                        className={`group relative p-2.5 border-2 rounded-lg transition-all ${
                          selectedCompetitors.includes(competitor.id)
                            ? "border-ink bg-surface"
                            : selectedCompetitors.length >= 3
                            ? "border-hairline bg-surface opacity-50"
                            : "border-hairline bg-surface-raised hover:border-ink-faint"
                        }`}
                      >
                        <div
                          onClick={() => toggleCompetitor(competitor.id)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                            {competitor.logo.startsWith("data:image") ? (
                              <img
                                src={competitor.logo}
                                alt={`${competitor.name} logo`}
                                className="w-full h-full object-contain rounded"
                              />
                            ) : (
                              <div className="text-xl">{competitor.logo}</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-raleway font-semibold text-xs text-ink truncate">
                              {competitor.name}
                            </h3>
                            <p className="font-raleway text-[10px] text-ink-muted truncate">
                              {competitor.domain}
                            </p>
                          </div>

                          {/* Checkbox and Delete Button Container */}
                          <div className="flex items-center">
                            {/* Checkbox - Slides Left on Hover */}
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-transform group-hover:-translate-x-3 ${
                                selectedCompetitors.includes(competitor.id)
                                  ? "bg-ink border-ink"
                                  : "border-hairline-strong"
                              }`}
                            >
                              {selectedCompetitors.includes(competitor.id) && (
                                <svg
                                  className="w-3 h-3 text-surface"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>

                            {/* Delete Button - Shows on Hover */}
                            {competitors.length > 1 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCompetitor(competitor.id);
                                }}
                                className="w-5 h-5 flex items-center justify-center text-red-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                title="Delete competitor"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Custom Competitor - Always Visible */}
              <div className="px-6 py-3 border-t border-hairline bg-surface-raised">
                {competitorError && (
                  <p className="text-red-500 text-xs mb-2 text-center">
                    {competitorError}
                  </p>
                )}
                {competitors.length >= 10 ? (
                  <p className="text-[10px] text-ink-muted font-raleway text-center italic">
                    Maximum competitors reached (10)
                  </p>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCompetitorName}
                      onChange={(e) => setNewCompetitorName(e.target.value)}
                      placeholder="Competitor name..."
                      className="flex-1 px-3 py-2 border-2 border-hairline-strong rounded-lg focus:outline-none focus:border-ink-faint font-raleway text-xs text-ink"
                    />
                    <input
                      type="text"
                      value={newCompetitorDomain}
                      onChange={(e) => setNewCompetitorDomain(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddCompetitor()
                      }
                      placeholder="Domain"
                      className="flex-1 px-3 py-2 border-2 border-hairline-strong rounded-lg focus:outline-none focus:border-ink-faint font-raleway text-xs text-ink"
                    />
                    <button
                      onClick={handleAddCompetitor}
                      disabled={
                        !newCompetitorName.trim() ||
                        !newCompetitorDomain.trim() ||
                        competitors.length >= 10 ||
                        isLoadingData
                      }
                      className="px-4 py-2 bg-ink/10 text-ink rounded-lg font-raleway font-semibold text-xs hover:bg-ink/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2"
                    >
                      {isLoadingData ? (
                        <>
                          <div className="w-4 h-4 border-2 border-ink-faint border-t-ink-muted rounded-full animate-spin"></div>
                          <span>Adding...</span>
                        </>
                      ) : (
                        "Add"
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Continue Button - Always Visible */}
              <div className="px-6 pb-3">
                <button
                  onClick={handleContinueToPrompts}
                  disabled={
                    competitors.length === 0 ||
                    selectedCompetitors.length === 0 ||
                    selectedCompetitors.length > 3
                  }
                  className="w-full bg-ink text-surface py-2 px-6 rounded-lg font-raleway font-semibold text-sm hover:bg-ink/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step C: Prompt Selection */}
          {currentStep === "prompts" && (
            <div
              className="flex flex-col"
              style={{ maxHeight: "calc(90vh - 80px)" }}
            >
              <div className="px-6 pt-3 pb-1">
                <h2 className="text-lg font-raleway font-bold text-ink mb-2 text-center">
                  Your potential customers are searching for these prompts.
                </h2>
                <p className="text-ink-muted font-raleway text-xs mb-1 text-center">
                  Select up to 3 prompts to track in your report
                </p>
                <div className="flex justify-center mb-2">
                  <span
                    className={`text-xs font-raleway font-semibold px-3 py-1 rounded-full ${
                      selectedPrompts.length === 0
                        ? "bg-red-100 text-red-700"
                        : selectedPrompts.length === 3
                        ? "bg-green-100 text-green-700"
                        : "bg-surface text-ink-muted"
                    }`}
                  >
                    {selectedPrompts.length === 0
                      ? "Please select at least 1"
                      : `${selectedPrompts.length} / 3 selected`}
                  </span>
                </div>
              </div>

              {/* Scrollable Prompts Area */}
              <div
                className="flex-1 overflow-y-auto px-6 pb-4"
                style={{ maxHeight: "calc(90vh - 320px)" }}
              >
                {prompts.length === 0 ? (
                  /* Empty State */
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-teal-100 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-10 h-10 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-raleway font-bold text-ink mb-2 text-center">
                      No Search Prompts Found
                    </h3>
                    <p className="text-sm text-ink-muted font-raleway text-center mb-4 max-w-md">
                      We couldn't identify customer search queries for your
                      industry. Please add at least 1 prompt below. You can
                      select up to 3 to track in your report.
                    </p>
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-2">
                      <svg
                        className="w-5 h-5 text-green-600 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      <p className="text-xs text-green-800 font-raleway">
                        <strong>Tip:</strong> Think about what questions your
                        potential customers ask when looking for solutions like
                        yours
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {prompts.map((prompt, index) => (
                      <div key={index} className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {editingIndex === index ? (
                            <>
                              <input
                                type="text"
                                value={editText}
                                onChange={(e) => {
                                  setEditText(e.target.value);
                                  setPromptError(""); // Clear error on input change
                                }}
                                onKeyPress={(e) =>
                                  e.key === "Enter" && handleSavePrompt()
                                }
                                className="flex-1 px-4 py-3 border-2 border-hairline-strong rounded-lg focus:outline-none focus:border-ink-faint font-raleway text-sm text-ink"
                                autoFocus
                              />
                              {/* Save (Check) Icon */}
                              <button
                                onClick={handleSavePrompt}
                                className="p-3 text-green-600 dark:text-green-400 hover:text-green-700 transition-colors cursor-pointer"
                                title="Save"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </button>
                              {/* Cancel (X) Icon */}
                              <button
                                onClick={() => {
                                  // If it's a new empty prompt, remove it
                                  if (
                                    editText.trim() === "" &&
                                    prompts[editingIndex] === ""
                                  ) {
                                    setPrompts(
                                      prompts.filter(
                                        (_, i) => i !== editingIndex
                                      )
                                    );
                                    // Update selected prompts indices after deletion
                                    setSelectedPrompts((prev) =>
                                      prev
                                        .filter((i) => i !== editingIndex)
                                        .map((i) =>
                                          i > editingIndex ? i - 1 : i
                                        )
                                    );
                                  }
                                  setEditingIndex(null);
                                  setEditText("");
                                  setPromptError(""); // Clear error on cancel
                                }}
                                className="p-3 text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                                title="Cancel"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </>
                          ) : (
                            <>
                              <div
                                onClick={() => togglePrompt(index)}
                                className={`flex-1 px-4 py-3 border-2 rounded-lg font-raleway text-sm bg-surface-raised flex items-center justify-between gap-2 transition-all cursor-pointer ${
                                  selectedPrompts.includes(index)
                                    ? "border-ink bg-surface"
                                    : selectedPrompts.length >= 3
                                    ? "border-hairline opacity-50 cursor-not-allowed"
                                    : "border-hairline hover:border-ink-faint"
                                }`}
                              >
                                <span className="flex-1 text-ink">
                                  {prompt || "Empty prompt"}
                                </span>
                                {/* Tracked indicator badge - only show if selected */}
                                {selectedPrompts.includes(index) && (
                                  <span className="flex items-center gap-1 text-green-600 text-xs font-semibold bg-green-50 px-2 py-1 rounded-full">
                                    <svg
                                      className="w-3 h-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Selected
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handleEditPrompt(index)}
                                className="px-3 py-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors cursor-pointer"
                                title="Edit prompt"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeletePrompt(index)}
                                className="px-3 py-3 text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                                title="Delete prompt"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                        {/* Error Message */}
                        {editingIndex === index && promptError && (
                          <p className="text-red-500 text-xs font-raleway px-4">
                            {promptError}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Prompt - Always Visible */}
              <div className="px-6 py-3 border-t border-hairline bg-surface-raised">
                {prompts.length >= 10 ? (
                  <p className="text-[10px] text-ink-muted font-raleway text-center italic">
                    Maximum prompts reached (10)
                  </p>
                ) : (
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newPromptText}
                      onChange={(e) => setNewPromptText(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddPrompt()}
                      placeholder="Enter new prompt..."
                      className="flex-1 px-3 py-2 border-2 border-hairline-strong rounded-lg focus:outline-none focus:border-ink-faint font-raleway text-xs text-ink"
                    />
                    <button
                      onClick={handleAddPrompt}
                      disabled={!newPromptText.trim() || prompts.length >= 10}
                      className="px-4 py-2 bg-ink/10 text-ink rounded-lg font-raleway font-semibold text-xs hover:bg-ink/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      Add
                    </button>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleBackToCompetitors}
                    disabled={isGeneratingReport}
                    className="px-6 py-2 border-2 border-hairline-strong text-ink rounded-lg font-raleway font-semibold text-sm hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleGenerateReport}
                    disabled={
                      prompts.length === 0 ||
                      selectedPrompts.length === 0 ||
                      selectedPrompts.length > 3 ||
                      isGeneratingReport
                    }
                    className="flex-1 bg-ink text-surface py-2 px-6 rounded-lg font-raleway font-semibold text-sm hover:bg-ink/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isGeneratingReport ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      "Generate Report"
                    )}
                  </button>
                </div>

                {/* Error Message */}
                {apiError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-xs font-raleway text-center">
                      {apiError}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step D: Success */}
          {currentStep === "success" && (
            <div className="p-8">
              <div className="text-center mb-8">
                {/* Score Circle/Badge */}
                <div className="relative w-32 h-32 mx-auto mb-6 flex items-center justify-center">
                  {/* Outer Ring */}
                  <div className="absolute inset-0 border-8 border-orange-100 dark:border-orange-500/20 rounded-full"></div>
                  {/* Inner Ring / Progress (Simulated) */}
                  <div className="absolute inset-0 border-8 border-orange-500 rounded-full border-t-transparent border-l-transparent rotate-45"></div>
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-ink font-raleway leading-none">
                      30–50%
                    </span>
                    <span className="text-[10px] font-bold text-orange-500 font-raleway uppercase tracking-wider mt-1">
                      GEO Score
                    </span>
                  </div>
                </div>

                {/* Main Message */}
                {/* <h2 className="text-xl font-raleway font-bold text-ink mb-2">
                                    Your Geo Score is currently estimated between 30–50%.
                                </h2> */}
                <h3 className="text-lg font-raleway font-semibold text-ink mb-6">
                  What does this mean for you?
                </h3>

                {/* What it means Section */}
                <div className="bg-surface rounded-xl p-5 border border-hairline text-left mb-6">
                  <ul className="space-y-4">
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 text-xl leading-none">
                        🔎
                      </span>
                      <p className="text-sm text-ink-muted font-raleway leading-snug m-0">
                        Your brand is{" "}
                        <span className="font-bold text-ink">
                          nearly invisible
                        </span>{" "}
                        in high‑intent AI discovery queries.
                      </p>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 text-xl leading-none">
                        📊
                      </span>
                      <p className="text-sm text-ink-muted font-raleway leading-snug m-0">
                        Competitors{" "}
                        <span className="font-bold text-ink">
                          dominate share‑of‑voice
                        </span>{" "}
                        in AI‑generated recommendations.
                      </p>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 text-xl leading-none">
                        ⚠️
                      </span>
                      <p className="text-sm text-ink-muted font-raleway leading-snug m-0">
                        Current AI sentiment places your product as a{" "}
                        <span className="font-bold text-ink">
                          secondary alternative
                        </span>{" "}
                        — not a market leader.
                      </p>
                    </li>
                  </ul>
                </div>

                {/* Call to Action */}
                <div className="mb-6">
                  <p className="text-base text-ink font-raleway font-semibold">
                    👉 Curious to know how this impacts your growth? Your full
                    report is on the way…
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/50 dark:to-purple-950/50 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-4">
                <h3 className="text-lg font-raleway font-bold text-ink mb-2 text-center">
                  While you wait... Let's talk strategy! 📞
                </h3>
                <p className="text-ink-muted font-raleway text-xs mb-4 text-center">
                  Your report will be ready in ~10 minutes. In the meantime,
                  book a strategy call to discuss how to dominate AI search.
                </p>
                <PopupButton
                  url="https://calendly.com/topgeo-sales/30min"
                  rootElement={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  text="Book a Strategy Call"
                  className="w-full bg-ink text-surface py-3 px-6 rounded-lg font-raleway font-bold text-sm hover:bg-ink/90 transition-all shadow-lg cursor-pointer"
                />
              </div>

              <button
                onClick={handleClose}
                className="w-full py-2 text-ink-muted font-raleway font-semibold text-sm hover:text-ink transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
